import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

const categoryOptions = [
  { value: 'adventure_desert', label: 'Adventure & Desert Tours' },
  { value: 'boat_cruise', label: 'Boat Trips & Luxury Cruises' },
  { value: 'city_breaks', label: 'City Breaks' },
  { value: 'cultural_historical', label: 'Cultural & Historical Tours' },
  { value: 'luxury_premium', label: 'Luxury & Premium Experiences' },
];

const locationOptions = [
  'hurghada', 'marsa_alam', 'luxor', 'aswan', 'cairo', 'sharm_el_sheikh'
];

const durationOptions = [
  { value: '1-4_hours', label: '1-4 Hours' },
  { value: '4-8_hours', label: '4-8 Hours' },
  { value: '1_day', label: '1 Day' },
  { value: '2_days', label: '2 Days' },
  { value: '3_days', label: '3 Days' },
];

const priceTypeOptions = [
  { value: 'group', label: 'Group Tour', description: 'Large groups (10-20 people)' },
  { value: 'small_group', label: 'Small Group Tour', description: 'Small groups (4-8 people)' },
  { value: 'private', label: 'Private Service', description: 'Private tour for 1-4 people' },
];

export default function ServiceForm({ isOpen, onClose, onSubmit, initialData }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    short_description: '',
    description: '',
    service_type: 'tour',
    category: 'adventure_desert',
    location: {
      city: 'hurghada',
      pickup_points: [],
    },
    duration: '4-8_hours',
    duration_hours: 6,
    prices: [],
    highlights: [],
    includes: [],
    excludes: [],
    images: [{ url: '', is_main: true, caption: '' }],
    status: 'active',
  });
  const [loading, setLoading] = useState(false);
  const [newPrice, setNewPrice] = useState({ 
    type: 'group', 
    name: '', 
    adult_price: 0,
    child_price: 0,
    toddler_price: 0,
    max_persons: 0, 
    min_persons: 0, 
    includes: [] 
  });
  const [newHighlight, setNewHighlight] = useState('');
  const [newInclude, setNewInclude] = useState('');
  const [newExclude, setNewExclude] = useState('');
  const [newPickupPoint, setNewPickupPoint] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        slug: initialData.slug || '',
        short_description: initialData.short_description || '',
        description: initialData.description || '',
        service_type: initialData.service_type || 'tour',
        category: initialData.category || 'adventure_desert',
        location: initialData.location || { city: 'hurghada', pickup_points: [] },
        duration: initialData.duration || '4-8_hours',
        duration_hours: initialData.duration_hours || 6,
        prices: initialData.prices || [],
        highlights: initialData.highlights || [],
        includes: initialData.includes || [],
        excludes: initialData.excludes || [],
        images: initialData.images || [{ url: '', is_main: true, caption: '' }],
        status: initialData.status || 'active',
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      setFormData({ ...formData, name: value, slug: generateSlug(value) });
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleAddPrice = () => {
    if (newPrice.name && newPrice.adult_price > 0) {
      setFormData({
        ...formData,
        prices: [...formData.prices, { ...newPrice, includes: newPrice.includes || [] }],
      });
      setNewPrice({ 
        type: 'group', 
        name: '', 
        adult_price: 0,
        child_price: 0,
        toddler_price: 0,
        max_persons: 0, 
        min_persons: 0, 
        includes: [] 
      });
    }
  };

  const handleRemovePrice = (index) => {
    setFormData({
      ...formData,
      prices: formData.prices.filter((_, i) => i !== index),
    });
  };

  const handleAddHighlight = () => {
    if (newHighlight.trim()) {
      setFormData({ ...formData, highlights: [...formData.highlights, newHighlight.trim()] });
      setNewHighlight('');
    }
  };

  const handleRemoveHighlight = (index) => {
    setFormData({
      ...formData,
      highlights: formData.highlights.filter((_, i) => i !== index),
    });
  };

  const handleAddInclude = () => {
    if (newInclude.trim()) {
      setFormData({ ...formData, includes: [...formData.includes, newInclude.trim()] });
      setNewInclude('');
    }
  };

  const handleRemoveInclude = (index) => {
    setFormData({
      ...formData,
      includes: formData.includes.filter((_, i) => i !== index),
    });
  };

  const handleAddExclude = () => {
    if (newExclude.trim()) {
      setFormData({ ...formData, excludes: [...formData.excludes, newExclude.trim()] });
      setNewExclude('');
    }
  };

  const handleRemoveExclude = (index) => {
    setFormData({
      ...formData,
      excludes: formData.excludes.filter((_, i) => i !== index),
    });
  };

  const handleAddPickupPoint = () => {
    if (newPickupPoint.trim() && !formData.location.pickup_points.includes(newPickupPoint.trim())) {
      setFormData({
        ...formData,
        location: {
          ...formData.location,
          pickup_points: [...formData.location.pickup_points, newPickupPoint.trim()],
        },
      });
      setNewPickupPoint('');
    }
  };

  const handleRemovePickupPoint = (point) => {
    setFormData({
      ...formData,
      location: {
        ...formData.location,
        pickup_points: formData.location.pickup_points.filter(p => p !== point),
      },
    });
  };

  const handleAddImage = () => {
    setFormData({
      ...formData,
      images: [...formData.images, { url: '', is_main: false, caption: '' }],
    });
  };

  const handleRemoveImage = (index) => {
    if (formData.images.length > 1) {
      setFormData({
        ...formData,
        images: formData.images.filter((_, i) => i !== index),
      });
    }
  };

  const handleImageChange = (index, field, value) => {
    const newImages = [...formData.images];
    newImages[index][field] = value;
    setFormData({ ...formData, images: newImages });
  };

  const handleSetMainImage = (index) => {
    const newImages = [...formData.images];
    newImages.forEach((_, i) => {
      newImages[i].is_main = i === index;
    });
    setFormData({ ...formData, images: newImages });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
          <h2 className="text-xl font-semibold">
            {initialData ? 'Edit Tour' : 'Add New Tour'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <FiX className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Tour Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Auto-generated from name</p>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
              <input
                type="text"
                name="short_description"
                value={formData.short_description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          {/* Category & Location */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              >
                {categoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
              <select
                name="location.city"
                value={formData.location.city}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              >
                {locationOptions.map(loc => (
                  <option key={loc} value={loc}>{loc.replace('_', ' ').toUpperCase()}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Pickup Points */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Points</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.location.pickup_points.map((point, idx) => (
                <span key={idx} className="bg-gray-100 px-3 py-1 rounded-full text-sm flex items-center">
                  {point}
                  <button
                    type="button"
                    onClick={() => handleRemovePickupPoint(point)}
                    className="ml-2 text-gray-500 hover:text-red-500"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add pickup point (e.g., Hurghada Airport, Makadi Bay Hotels)"
                value={newPickupPoint}
                onChange={(e) => setNewPickupPoint(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddPickupPoint()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddPickupPoint}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>

          {/* Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              >
                {durationOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Hours)</label>
              <input
                type="number"
                name="duration_hours"
                value={formData.duration_hours}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
            </div>
          </div>

          {/* Prices - Updated for age-based pricing */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Pricing Options</label>
            <div className="space-y-2 mb-3">
              {formData.prices.map((price, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{price.name}</p>
                    <p className="text-sm text-gray-600">
                      Adult: ${price.adult_price} | Child (6-12): ${price.child_price || 0} | Toddler (2-6): ${price.toddler_price || 0}
                    </p>
                    <p className="text-sm text-gray-600">{price.type.replace('_', ' ')}</p>
                    {price.max_persons > 0 && (
                      <p className="text-xs text-gray-500">Max: {price.max_persons} persons | Min: {price.min_persons} persons</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemovePrice(idx)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2">
              <select
                value={newPrice.type}
                onChange={(e) => setNewPrice({ ...newPrice, type: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              >
                {priceTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Name (e.g., Group Tour)"
                value={newPrice.name}
                onChange={(e) => setNewPrice({ ...newPrice, name: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <input
                type="number"
                placeholder="Adult Price"
                value={newPrice.adult_price}
                onChange={(e) => setNewPrice({ ...newPrice, adult_price: parseFloat(e.target.value) })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddPrice}
                disabled={!newPrice.name || newPrice.adult_price <= 0}
                className="px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                <FiPlus />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <input
                type="number"
                placeholder="Child Price (6-12)"
                value={newPrice.child_price}
                onChange={(e) => setNewPrice({ ...newPrice, child_price: parseFloat(e.target.value) })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="number"
                placeholder="Toddler Price (2-6)"
                value={newPrice.toddler_price}
                onChange={(e) => setNewPrice({ ...newPrice, toddler_price: parseFloat(e.target.value) })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="number"
                placeholder="Max Persons"
                value={newPrice.max_persons}
                onChange={(e) => setNewPrice({ ...newPrice, max_persons: parseInt(e.target.value) })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
              <input
                type="number"
                placeholder="Min Persons"
                value={newPrice.min_persons}
                onChange={(e) => setNewPrice({ ...newPrice, min_persons: parseInt(e.target.value) })}
                className="px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* Highlights */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Highlights</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.highlights.map((highlight, idx) => (
                <span key={idx} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center">
                  {highlight}
                  <button
                    type="button"
                    onClick={() => handleRemoveHighlight(idx)}
                    className="ml-2 text-blue-500 hover:text-red-500"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add highlight (e.g., Quad bike adventure, Jeep safari)"
                value={newHighlight}
                onChange={(e) => setNewHighlight(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddHighlight()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddHighlight}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>

          {/* Includes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What's Included</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.includes.map((include, idx) => (
                <span key={idx} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm flex items-center">
                  ✓ {include}
                  <button
                    type="button"
                    onClick={() => handleRemoveInclude(idx)}
                    className="ml-2 text-green-500 hover:text-red-500"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g., Hotel pickup, Lunch, Guide"
                value={newInclude}
                onChange={(e) => setNewInclude(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddInclude()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddInclude}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>

          {/* Excludes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">What's Excluded</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.excludes.map((exclude, idx) => (
                <span key={idx} className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm flex items-center">
                  ✗ {exclude}
                  <button
                    type="button"
                    onClick={() => handleRemoveExclude(idx)}
                    className="ml-2 text-red-500 hover:text-red-700"
                  >
                    <FiTrash2 className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="e.g., Alcoholic drinks, Tips, Personal expenses"
                value={newExclude}
                onChange={(e) => setNewExclude(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddExclude()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
              />
              <button
                type="button"
                onClick={handleAddExclude}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
          </div>

          {/* Images */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
            {formData.images.map((img, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="url"
                  placeholder="Image URL"
                  value={img.url}
                  onChange={(e) => handleImageChange(idx, 'url', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                />
                <input
                  type="text"
                  placeholder="Caption"
                  value={img.caption}
                  onChange={(e) => handleImageChange(idx, 'caption', e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-primary-500"
                />
                <label className="flex items-center px-3">
                  <input
                    type="radio"
                    name="main_image"
                    checked={img.is_main}
                    onChange={() => handleSetMainImage(idx)}
                    className="mr-1"
                  />
                  Main
                </label>
                <button
                  type="button"
                  onClick={() => handleRemoveImage(idx)}
                  className="text-red-500 hover:text-red-700"
                  disabled={formData.images.length === 1}
                >
                  <FiTrash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddImage}
              className="text-primary-500 hover:text-primary-600 text-sm flex items-center mt-2"
            >
              <FiPlus className="mr-1" /> Add Image
            </button>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="active"
                  checked={formData.status === 'active'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Active
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="inactive"
                  checked={formData.status === 'inactive'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Inactive
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="status"
                  value="draft"
                  checked={formData.status === 'draft'}
                  onChange={handleChange}
                  className="mr-2"
                />
                Draft
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 transition"
            >
              {loading ? 'Saving...' : initialData ? 'Update Tour' : 'Create Tour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
